import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { LogOut, Mail, RefreshCcw, ArrowUpRight, Loader2 } from "lucide-react";
import { FOUNDATION_LOGO } from "@/lib/initiatives";

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-[#0A2540]/10 p-5">
      <div className="text-xs uppercase tracking-[0.16em] text-[#4A5568]">{label}</div>
      <div className="mt-2 font-serif-display text-3xl text-[#0A2540]">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [donations, setDonations] = useState([]);
  const [csr, setCsr] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [emails, setEmails] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false) nav("/admin/login", { replace: true });
  }, [user, nav]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d, c, m, e, v, n] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/donations"),
        api.get("/admin/csr-enquiries"),
        api.get("/admin/contact-messages"),
        api.get("/admin/email-log"),
        api.get("/admin/volunteers"),
        api.get("/admin/newsletter"),
      ]);
      setStats(s.data);
      setDonations(d.data);
      setCsr(c.data);
      setContacts(m.data);
      setEmails(e.data);
      setVolunteers(v.data);
      setNewsletter(n.data);
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") loadAll();
  }, [user, loadAll]);

  const resend = async (id) => {
    try {
      await api.post(`/admin/donations/${id}/resend-receipt`);
      toast.success("Receipt resent (logged).");
      loadAll();
    } catch (err) {
      toast.error("Could not resend receipt");
    }
  };

  const doLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  if (!user || user === false)
    return (
      <div className="section-py flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" />
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="bg-white border-b border-[#0A2540]/10">
        <div className="mx-auto max-w-7xl container-x flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden">
              <img src={FOUNDATION_LOGO} alt="UBMF" className="w-full h-full object-cover" />
            </div>
            <div className="leading-tight">
              <div className="font-serif-display text-[#0A2540]">UB Maitreya</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#4A5568]">Admin Console</div>
            </div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:inline text-[#4A5568]">{user.email}</span>
            <Button
              variant="outline"
              onClick={loadAll}
              data-testid="admin-refresh-btn"
              className="rounded-full border-[#0A2540]/20"
            >
              <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={doLogout}
              data-testid="admin-logout-btn"
              className="rounded-full bg-[#0A2540] text-white hover:bg-[#D4AF37] hover:text-[#0A2540]"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl container-x py-10">
        <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">Overview</div>
        <h1 className="mt-2 font-serif-display text-3xl md:text-4xl text-[#0A2540]">Foundation Dashboard</h1>

        {loading && !stats ? (
          <div className="mt-8 flex items-center gap-2 text-[#4A5568]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="admin-stats">
              <Stat label="Total Donations" value={stats?.total_donations ?? 0} />
              <Stat label="Total Raised (₹)" value={`₹${Number(stats?.total_amount_inr ?? 0).toLocaleString("en-IN")}`} />
              <Stat label="Pending" value={stats?.pending_donations ?? 0} />
              <Stat label="CSR Enquiries" value={stats?.csr_enquiries ?? 0} />
              <Stat label="Messages" value={stats?.contact_messages ?? 0} />
              <Stat label="Volunteers" value={stats?.volunteers ?? 0} />
              <Stat label="Newsletter" value={stats?.newsletter ?? 0} />
            </div>

            <div className="mt-10">
              <Tabs defaultValue="donations">
                <TabsList className="bg-white border border-[#0A2540]/10 rounded-full p-1 flex-wrap">
                  <TabsTrigger value="donations" data-testid="tab-donations" className="rounded-full data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Donations</TabsTrigger>
                  <TabsTrigger value="csr" data-testid="tab-csr" className="rounded-full data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">CSR</TabsTrigger>
                  <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-full data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Messages</TabsTrigger>
                  <TabsTrigger value="volunteers" data-testid="tab-volunteers" className="rounded-full data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Volunteers</TabsTrigger>
                  <TabsTrigger value="newsletter" data-testid="tab-newsletter" className="rounded-full data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Newsletter</TabsTrigger>
                  <TabsTrigger value="emails" data-testid="tab-emails" className="rounded-full data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">Email Log</TabsTrigger>
                </TabsList>

                <TabsContent value="donations" className="mt-6">
                  <div className="overflow-x-auto rounded-2xl bg-white border border-[#0A2540]/10">
                    <table className="w-full text-sm">
                      <thead className="text-left text-[#4A5568] uppercase text-xs tracking-[0.16em]">
                        <tr>
                          <th className="px-4 py-3">Receipt</th>
                          <th className="px-4 py-3">Donor</th>
                          <th className="px-4 py-3">Amount</th>
                          <th className="px-4 py-3">Cause</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {donations.length === 0 && (
                          <tr><td colSpan="7" className="px-4 py-6 text-center text-[#4A5568]">No donations yet.</td></tr>
                        )}
                        {donations.map((d) => (
                          <tr key={d.id} className="border-t border-[#0A2540]/10" data-testid={`donation-row-${d.id}`}>
                            <td className="px-4 py-3 font-medium text-[#0A2540]">{d.receipt_no || "—"}</td>
                            <td className="px-4 py-3">
                              <div className="text-[#0A2540]">{d.donor_name}</div>
                              <div className="text-xs text-[#4A5568]">{d.donor_email}</div>
                            </td>
                            <td className="px-4 py-3 font-medium">₹{Number(d.amount).toLocaleString("en-IN")}</td>
                            <td className="px-4 py-3">{d.cause}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                d.status === "completed" ? "bg-[#38A169]/10 text-[#276749]" : "bg-[#DD6B20]/10 text-[#9C4221]"
                              }`}>{d.status}</span>
                            </td>
                            <td className="px-4 py-3 text-xs text-[#4A5568]">
                              {new Date(d.completed_at || d.created_at).toLocaleString("en-IN")}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {d.status === "completed" && (
                                <button
                                  onClick={() => resend(d.id)}
                                  data-testid={`resend-${d.id}`}
                                  className="inline-flex items-center gap-1 text-xs text-[#0A2540] hover:text-[#D4AF37]"
                                >
                                  <Mail className="w-3.5 h-3.5" /> Resend
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                <TabsContent value="csr" className="mt-6">
                  <div className="overflow-x-auto rounded-2xl bg-white border border-[#0A2540]/10">
                    <table className="w-full text-sm">
                      <thead className="text-left text-[#4A5568] uppercase text-xs tracking-[0.16em]">
                        <tr>
                          <th className="px-4 py-3">Company</th>
                          <th className="px-4 py-3">Contact</th>
                          <th className="px-4 py-3">Budget</th>
                          <th className="px-4 py-3">Initiatives</th>
                          <th className="px-4 py-3">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csr.length === 0 && (
                          <tr><td colSpan="5" className="px-4 py-6 text-center text-[#4A5568]">No CSR enquiries yet.</td></tr>
                        )}
                        {csr.map((c) => (
                          <tr key={c.id} className="border-t border-[#0A2540]/10">
                            <td className="px-4 py-3 font-medium text-[#0A2540]">{c.company_name}</td>
                            <td className="px-4 py-3">
                              <div>{c.contact_person}</div>
                              <div className="text-xs text-[#4A5568]">{c.email} · {c.phone}</div>
                            </td>
                            <td className="px-4 py-3">{c.budget_range || "—"}</td>
                            <td className="px-4 py-3 text-xs">{(c.interested_initiatives || []).join(", ") || "—"}</td>
                            <td className="px-4 py-3 text-xs text-[#4A5568]">
                              {new Date(c.created_at).toLocaleString("en-IN")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                <TabsContent value="contacts" className="mt-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    {contacts.length === 0 && <div className="text-[#4A5568]">No contact messages yet.</div>}
                    {contacts.map((m) => (
                      <div key={m.id} className="rounded-2xl bg-white border border-[#0A2540]/10 p-5">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium text-[#0A2540]">{m.name}</div>
                            <div className="text-xs text-[#4A5568]">{m.email} · {m.phone || "—"}</div>
                          </div>
                          <div className="text-xs text-[#4A5568]">
                            {new Date(m.created_at).toLocaleDateString("en-IN")}
                          </div>
                        </div>
                        {m.subject && <div className="mt-3 text-sm text-[#0A2540]">{m.subject}</div>}
                        <p className="mt-2 text-sm text-[#4A5568] whitespace-pre-wrap">{m.message}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="volunteers" className="mt-6">
                  <div className="overflow-x-auto rounded-2xl bg-white border border-[#0A2540]/10">
                    <table className="w-full text-sm">
                      <thead className="text-left text-[#4A5568] uppercase text-xs tracking-[0.16em]">
                        <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">City</th><th className="px-4 py-3">Availability</th><th className="px-4 py-3">Interests</th><th className="px-4 py-3">Date</th></tr>
                      </thead>
                      <tbody>
                        {volunteers.length === 0 && <tr><td colSpan="6" className="px-4 py-6 text-center text-[#4A5568]">No volunteers yet.</td></tr>}
                        {volunteers.map((v) => (
                          <tr key={v.id} className="border-t border-[#0A2540]/10">
                            <td className="px-4 py-3 font-medium text-[#0A2540]">{v.name}</td>
                            <td className="px-4 py-3 text-xs"><div>{v.email}</div><div className="text-[#4A5568]">{v.phone}</div></td>
                            <td className="px-4 py-3">{v.city || "—"}</td>
                            <td className="px-4 py-3">{v.availability || "—"}</td>
                            <td className="px-4 py-3 text-xs">{(v.interests || []).join(", ") || "—"}</td>
                            <td className="px-4 py-3 text-xs text-[#4A5568]">{new Date(v.created_at).toLocaleString("en-IN")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                <TabsContent value="newsletter" className="mt-6">
                  <div className="overflow-x-auto rounded-2xl bg-white border border-[#0A2540]/10">
                    <table className="w-full text-sm">
                      <thead className="text-left text-[#4A5568] uppercase text-xs tracking-[0.16em]">
                        <tr><th className="px-4 py-3">Email</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Date</th></tr>
                      </thead>
                      <tbody>
                        {newsletter.length === 0 && <tr><td colSpan="3" className="px-4 py-6 text-center text-[#4A5568]">No subscribers yet.</td></tr>}
                        {newsletter.map((n) => (
                          <tr key={n.id} className="border-t border-[#0A2540]/10">
                            <td className="px-4 py-3 font-medium text-[#0A2540]">{n.email}</td>
                            <td className="px-4 py-3">{n.name || "—"}</td>
                            <td className="px-4 py-3 text-xs text-[#4A5568]">{new Date(n.created_at).toLocaleString("en-IN")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>


                <TabsContent value="emails" className="mt-6">
                  <div className="rounded-2xl bg-white border border-[#0A2540]/10 divide-y divide-[#0A2540]/10">
                    {emails.length === 0 && <div className="p-6 text-[#4A5568]">No emails logged yet.</div>}
                    {emails.map((e) => (
                      <details key={e.id} className="p-5">
                        <summary className="cursor-pointer flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <Mail className="w-4 h-4 text-[#D4AF37]" />
                            <div>
                              <div className="font-medium text-[#0A2540] text-sm">{e.subject}</div>
                              <div className="text-xs text-[#4A5568]">To: {e.to}</div>
                            </div>
                          </div>
                          <div className="text-xs text-[#4A5568]">{new Date(e.sent_at).toLocaleString("en-IN")}</div>
                        </summary>
                        <pre className="mt-4 whitespace-pre-wrap text-xs text-[#1A1A1A] bg-[#F9F8F6] rounded-xl p-4 border border-[#0A2540]/10 max-h-96 overflow-auto">{e.body}</pre>
                      </details>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
